import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    id?: string
    eyebrow?: string
    title: string
    description?: string
    action?: {
        label: string
        href: string
    }
    children?: ReactNode
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    eyebrow,
    title,
    description,
    action,
    children,
    className
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between', className)}>
            <div className='flex max-w-2xl flex-col gap-1.5'>
                {eyebrow && (
                    <span className='text-small font-semibold text-primary-strong'>
                        {eyebrow}
                    </span>
                )}

                <h2 id={id} className='text-h2 text-navy'>
                    {title}
                </h2>

                {description && (
                    <p className='text-body text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            {(action || children) && (
                <div className='flex shrink-0 flex-wrap items-center gap-3'>
                    {children}

                    {action && (
                        <Link
                            href={action.href}
                            className='group inline-flex items-center gap-1.5 rounded-sm text-sm font-semibold text-primary-strong transition-colors hover:text-primary-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45'
                        >
                            {action.label}
                            <ArrowLeft className='size-4 transition-transform duration-200 group-hover:-translate-x-0.5' aria-hidden='true' />
                        </Link>
                    )}
                </div>
            )}
        </div>
    )
}
